<template>
  <!-- eslint-disable vuejs-accessibility/form-control-has-label -->
  <!--
    Single root wrapper: Vue 3.5.12's block patcher mishandles dynamic children of a
    fragment when a top-level component vnode (e.g. ProcessingSpinner via v-if) toggles
    in the same flush as other reactive updates, surfacing as
    "Cannot read properties of null (reading 'emitsOptions')" in shouldUpdateComponent.
  -->
  <div>
    <!--
      These three are mutually-exclusive full-screen overlays over the always-rendered
      CartHeader/cart-body (which toggle via :style visibility, not v-if). They use
      v-show rather than a v-if/v-else-if chain on purpose: toggling component vnodes in
      the chain mid-flush made Vue's block patcher insert a v-else-if comment anchor into
      a null parent ("Cannot read properties of null (reading 'insertBefore')") when a
      submit failed. v-show keeps them mounted and avoids the comment-anchor churn.
    -->
    <!-- Loading spinner -->
    <!--
      v-show sets an inline `display: none`, which Bootstrap's `.d-flex`
      (`display: flex !important`) would override, pinning the spinner on screen.
      Keep the toggled element free of !important display utilities and put the
      flex centering on an inner wrapper instead.
    -->
    <div v-show="state.formVisibility === 'hidden'" class="container">
      <div class="d-flex justify-content-center">
        <CenteredBootstrapSpinner />
      </div>
    </div>
    <!-- Loading failed -->
    <LoadingFailedAlert v-show="state.formVisibility === 'error'" />
    <!-- Payment processing spinner -->
    <ProcessingSpinner v-show="paymentState.processing" />
    <!-- main form shown after all data has been fetched -->
    <CartHeader
      :style="{
        visibility: state.formVisibility,
      }"
    />
    <div
      class="container justify-content-center cart-body row"
      :style="{
        visibility: state.formVisibility,
      }"
    >
      <PaidSubAlert v-if="checkoutData.isPaid && !ignoreUsersPaidStatus" />
      <AddedTeamAlert v-if="route.query.addedTeam" />
      <RemovedServerAlert v-if="Number(route.query['removed-servers']) === 1" />
      <RouterCouponAlert v-if="couponIsVendorRouter || state.showVendorRouterCouponAlert" />
      <PlanChangeDialog
        v-if="state.checkout.showPlanChangeDialog" 
        :couponPlanName="state.checkout.couponPlanName"
        :couponCode="checkoutData.coupon.inputtedCode"
        :couponPlanPath="state.checkout.couponPlanPath"
      />
      <form @submit.prevent="submit" ref="form" id="form" class="row d-flex flex-wrap justify-content-around align-content-start align-items-start">
        <div class="col-md-5 col-12 order-md-2 float-left p-3">
          <UpsellCard v-if="!checkoutData.coupon.success && state.pricing.discount === 0" />
          <section class="price bg-light rounded pt-1">
            <div id="checkout-cart" class="s-cart px-4 pt-1 pb-4">
              <h4 class="d-flex justify-content-between align-items-center mb-3 mt-3 s-h4 fw-bold">
                <span>Order Summary</span>
              </h4>
              <ul class="list-group list-group-flush cart-group mb-3 border-none">
                <li class="list-group-item cart-item justify-content-between lh-condensed bg-light">
                  <div class="justify-content-between d-flex flex-nowrap">
                    <div class="text-start">
                      <span id="plan-name" class="my-0 text-start">{{ state.plan.name }}</span>
                    </div>
                    <div class="text-end">
                      <span class="text-muted">{{ convertToCountryLocaleCurrency(state.pricing.plan) }}</span>
                    </div>
                  </div>
                  <div
                    class="w-100 text-start d-flex flex-nowrap justify-content-end align-items-middle small position-relative"
                    v-if="subscriptionProductEligibleForQuantityChange"
                  >
                    <span class="my-auto">Qty:&nbsp;</span>
                    <span class="text-end qty-selector">
                      <input
                        type="number"
                        id="plan-quantity"
                        data-recurly-subscription="0"
                        data-recurly="plan_quantity"
                        v-model="checkoutData.quantity"
                        class="form-control"
                      />
                    </span>
                    <transition name="qty-pop">
                      <div v-if="showQtyLimitPopover" class="qty-limit-popover" role="status">
                        <button
                          type="button"
                          class="qty-limit-popover__close"
                          aria-label="Dismiss"
                          @click="showQtyLimitPopover = false"
                        >&times;</button>
                        <p class="qty-limit-popover__title mb-1">Max {{ quantityLimit }} per order</p>
                        <p class="mb-0">
                          You can add more licenses later, or
                          <a href="mailto:support@speedify.com">contact us</a>
                          for a bigger order.
                        </p>
                      </div>
                    </transition>
                  </div>
                  <div class="d-flex justify-content-between" v-if="freeTrialActive">
                    <span class="text-muted text-start">
                      Try Speedify for {{ checkoutData.trialLength }} for free on us!
                    </span>
                  </div>
                </li>
                <li
                  id="dedicated-server-details"
                  class="list-group-item cart-item justify-content-between lh-condensed bg-light"
                  v-if="serversAvailable"
                >
                  <div class="justify-content-between d-flex flex-nowrap">
                    <div class="text-start">
                      <span id="dedicated-server-name" class="my-0">{{ checkoutData.serverName }}</span>
                    </div>
                    <div>
                      <span class="text-muted">{{ convertToCountryLocaleCurrency(state.pricing.servers) }}</span>
                    </div>
                  </div>
                  <div class="w-100 text-start d-flex flex-nowrap justify-content-end align-items-middle small position-relative">
                    <button
                      v-if="!disableRemoveServerButton"
                      class="btn text-muted"
                      type="button"
                      id="remove-server"
                      @click="handleRemoveServerClick"
                    >
                      Remove
                    </button>
                    <span class="my-auto">Qty:&nbsp;</span>
                    <span id="server-plan-quantity" class="text-end qty-selector">
                      <input
                        type="number"
                        id="server-quantity"
                        data-recurly-subscription="1"
                        data-recurly="plan_quantity"
                        v-model="checkoutData.servers"
                        class="form-control"
                      />
                    </span>
                    <transition name="qty-pop">
                      <div v-if="showServersLimitPopover" class="qty-limit-popover" role="status">
                        <button
                          type="button"
                          class="qty-limit-popover__close"
                          aria-label="Dismiss"
                          @click="showServersLimitPopover = false"
                        >&times;</button>
                        <p class="qty-limit-popover__title mb-1">Max {{ quantityLimit }} per order</p>
                        <p class="mb-0">
                          You can add more servers later, or
                          <a href="mailto:support@speedify.com">contact us</a>
                          for a bigger order.
                        </p>
                      </div>
                    </transition>
                  </div>
                </li>
                <li
                  id="promo-details"
                  v-if="state.pricing.discount > 0 || freeTrialActive"
                  class="list-group-item cart-item d-flex flex-nowrap justify-content-between bg-light text-success"
                >
                  <div class="text-start">
                    <span class="my-0">Discount</span>
                    <small id="coupon-description"></small>
                  </div>
                  <div v-if="freeTrialActive">
                    <span class="text-success">{{ checkoutData.trialLength }} Free Trial</span>
                  </div>
                  <div v-else>
                    <span id="discount-amount" class="text-success">{{ convertToCountryLocaleCurrency(state.pricing.discount) }}</span>
                  </div>
                </li>
                <li
                  id="tax-line-item"
                  class="list-group-item cart-item d-flex justify-content-between bg-light"
                  v-if="state.pricing.taxes > 0"
                >
                  <div class="text-start">
                    <h6 class="my-0">
                      {{ state.vat.applicable ? 'VAT' : 'Tax' }}
                    </h6>
                  </div>
                  <span id="tax-amount" class="text-muted">
                    {{ convertToCountryLocaleCurrency(state.pricing.taxes) }}
                  </span>
                </li>
                <li class="list-group-item cart-item d-flex justify-content-between bg-light">
                  <span id="total-descriptor" class="fw-bold">Total ({{ checkoutData.currency }})</span>
                  <div>
                    <strong>{{ convertToCountryLocaleCurrency(checkoutData.totalPrice) }}</strong>
                  </div>
                </li>
                <li
                  id="after-trial-total"
                  class="list-group-item cart-item d-flex justify-content-between bg-light"
                  v-if="state.pricing.chargedAfterTrial"
                >
                  <span class="text-muted">Total after trial ({{ checkoutData.currency }})</span>
                  <span class="text-muted">{{ convertToCountryLocaleCurrency(state.pricing.totalAfterTrial) }}</span>
                </li>
              </ul>
              <div class="gap-3 d-flex flex-column">
                <div class="d-flex flex-nowrap justify-content-between gap-2" v-if="state.showCouponInput || props.coupon">
                  <input
                    type="text"
                    class="form-control"
                    id="redeem"
                    placeholder="Coupon"
                    name="couponInput"
                    label="Coupon"
                    @keyup.enter="applyCoupon"
                    v-model="checkoutData.coupon.inputtedCode"
                  />
                  <div class="input-group-append">
                    <button id="applyCouponBtn" @click="applyCoupon" type="button" class="btn btn-secondary s-redeem-btn stm-btn-coupon-redeem">Apply</button>
                  </div>
                </div>
                <div 
                  class="d-flex flex-nowrap justify-content-between gap-2"
                  v-if="state.vat.vatNumInputVisible && state.vat.applicable"
                >
                  <input
                    type="text"
                    class="form-control"
                    id="vat_number"
                    placeholder="VAT Number (optional)"
                    name="vatNumber"
                    label="VatNumber"
                    @keyup.enter="applyVatNumber"
                    v-model="state.vat.vatNumber"
                    :disabled="state.vat.exemptionApplied"
                  />
                  <div class="input-group-append">
                    <button 
                      id="applyVatBtn"
                      @click="applyVatNumber"
                      type="button"
                      class="btn btn-secondary s-redeem-btn stm-btn-vat-apply"
                      v-if="!state.vat.exemptionApplied"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
              <div class="alert alert-danger mt-3 mb-0 stm-msg-coupon-error" role="alert" v-if="checkoutData.coupon.invalid">
                <i class="fas fa-exclamation-circle"></i>
                &nbsp;{{ checkoutData.coupon.invalidMessage }}
              </div>
              <div class="alert alert-success mt-3 mb-0 stm-msg-coupon-success" role="alert" v-if="checkoutData.coupon.success">
                <i class="fas fa-check-circle"></i>
                &nbsp;Coupon has been applied!
              </div>
              <div class="alert alert-success mt-3 mb-0 stm-msg-coupon-success" role="alert" v-if="state.vat.exemptionApplied">
                <i class="fas fa-check-circle"></i>
                &nbsp;VAT exemption has been applied!
              </div>
              <div class="alert alert-danger mt-3 mb-0 stm-msg-coupon-error" role="alert" v-if="state.vat.invalidNumber && state.vat.applicable">
                <i class="fas fa-exclamation-circle"></i>
                &nbsp;Invalid VAT number
              </div>
              <div class="hstack gap-2 d-flex justify-content-center py-2">
                <button v-if="!state.showCouponInput && !props.coupon" @click="showCouponInput" type="button" class="btn btn-link">Have a coupon code?</button>
                <div v-if="!state.showCouponInput && !state.vat.vatNumInputVisible && state.vat.applicable && !props.coupon" class="vr"></div>
                <button
                  @click="state.vat.vatNumInputVisible = true" 
                  type="button" 
                  class="btn btn-link"
                  v-if="state.vat.applicable && !state.vat.vatNumInputVisible"
                >
                  VAT exempt?
                </button>
              </div>
            </div>
          </section>
        </div>
        <div class="col-md-7 col-12 order-md-1 float-left flex-row justify-content-left text-start mb-5">
          <section>
            <AccountSelection 
              :displayFormError="state.displayFormError"
              :invalidEmail="v$.checkoutData.email.$invalid"
              :invalidEmailRepeat="v$.checkoutData.emailRepeat.$invalid"
            />
            <hr class="mb-4" />
            <h4 id="free-trial-header" class="mb-3 s-h4 fw-bold" v-if="state.checkout.hideBillingForm">2: Complete Captcha</h4>
            <h4 id="payment-header" class="mb-3 s-h4 fw-bold" v-else>2: Select Payment</h4>
            <div class="form-check mb-3">
              <input
                type="checkbox"
                class="form-check-input"
                id="tos-privacy"
                v-model="state.checkout.tosPrivacyAccepted"
              />
              <label class="form-check-label small" for="tos-privacy">
                I have read and accept the
                <a href="https://speedify.com/terms-of-service/" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                and
                <a href="https://speedify.com/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </label>
              <div>
                <small style="color: red" v-if="v$.state.checkout.tosPrivacyAccepted.$invalid && state.displayFormError">
                  Please accept the Terms of Service and Privacy Policy to continue.
                </small>
              </div>
            </div>
            <div v-if="subscriptionProductRequiresCoupon && !state.checkout.hideBillingForm && state.formVisibility === 'visible'">
              <div class="alert alert-danger">
                This product can only be purchased or redeemed with a valid coupon.
              </div>
            </div>
            <div v-else-if="state.checkout.hideBillingForm" class="border shadow p-4">
              <!-- TODO: turn this into a reusable component -->
              <div v-if="state.checkout.turnstileSupported">
                <div>
                  <VueTurnstile
                    :site-key="turnstileSiteKey"
                    v-model="state.checkout.captchaResponse"
                    size="flexible"
                    ref="turnstileWidget"
                    @expired="turnstileExpired"
                    @unsupported="turnstileUnsupported"
                    @error="turnstileError"
                  />
                </div>
                <small style="color: red" class="mb-3" v-if="v$.state.checkout.captchaDone.$invalid && state.displayFormError">
                  Please complete the captcha challenge.
                </small>
                <small style="color: red" class="mb-3" v-if="state.checkout.turnstileError">
                  Captcha challenge encountered an error. Please reload the page or try again later.
                </small>
              </div>
              <div class="w-100 d-grid gap-2 mt-3" @click="showVuelidateErrors" @keydown="showVuelidateErrors">
                <div class="alert alert-danger mt-3 stm-msg-checkout-error" role="alert" v-if="state.braintree.error">
                  <span>{{ state.braintree.error }}</span>
                </div>
                <div class="alert alert-danger mt-3 stm-msg-checkout-error" role="alert" v-if="paymentState.purchaseFailed || paymentState.purchasePartiallySucceeded">
                  <i class="fas fa-exclamation-circle"></i>
                  &nbsp;Error
                  <hr />
                  <p v-if="paymentState.purchaseFailed">
                    Unable to process order:
                    <br />
                    <span>{{ panel.methodError }}</span>
                  </p>
                  <p v-else-if="paymentState.purchasePartiallySucceeded">
                    Purchase has been partially applied. Please note that the following products failed to be applied to your account. You have not been charged for them.
                    <br />
                    <br />
                    <span v-for="err in panel.methodError" :key="err.planCode">{{ formatPlanCode(err.planCode) }}: {{ err.err }}</span>
                  </p>
                </div>
                <button
                  type="submit"
                  class="btn btn-primary btn-lg stm-btn-order-submit"
                  @click="subscribe"
                  :disabled="v$.$validationGroups.freeCardlessTrial.$invalid"
                >
                  Start My Free Trial
                </button>
              </div>
            </div>
            <div class="accordion payment-accordion" id="paymentAccordion" v-else>
              <div 
                class="accordion-item col-12"
                v-if="enableApplePay && checkoutPricing && state.braintree.ready"
              >
                <ApplePayAccordionPanel 
                  :recurlyPricing="checkoutPricing"
                  :plan="state.plan.name"
                  :disableButton="v$.$validationGroups.applePay.$invalid"
                  @hideApplePay="disableApplePay"
                  @showApplePayVuelidate="showVuelidateErrors"
                  @paymentProcessing="showProcessingSpinner"
                  @paymentFailed="hideProcessingSpinner"
                />
              </div>
              <div class="accordion-item col-12">
                <h4 class="accordion-header" id="headingOne">
                  <button
                    type="button"
                    class="accordion-button collapsed d-flex flex-row justify-content-between gap-2 stm-btn-accordion-credit-card"
                    data-bs-toggle="collapse"
                    data-bs-target="#accordion-2"
                    aria-expanded="false"
                    aria-controls="accordion-2"
                  >
                    <div class="d-flex justify-content-between w-100">
                      <div>Credit or Debit</div>
                      <div>
                        <img alt="Visa supported" class="icon" :src="ccVisaSvg" />
                        <img alt="Mastercard supported" class="icon" :src="ccMastercardSvg" />
                        <img alt="Amex supported" class="icon" :src="ccAmexSvg" />
                        <img alt="Discover supported" class="icon" :src="ccDiscoverSvg" />
                      </div>
                    </div>
                  </button>
                </h4>
                <div id="accordion-2" class="accordion-collapse collapse" aria-labelledby="headingOne" data-bs-parent="#paymentAccordion">
                  <div class="accordion-body card-element row" style="overflow: overlay">
                    <div class="col-12 col-md-6 my-2">
                      <label for="first_name" class="small">First Name</label>
                      <input
                        type="input"
                        data-recurly="first_name"
                        id="first_name"
                        name="first-name"
                        v-model="checkoutData.firstName"
                        class="form-control"
                      />
                      <small style="color: red" v-if="v$.checkoutData.firstName.$invalid && state.displayFormError">
                        First name cannot be blank.
                      </small>
                    </div>
                    <div class="col-12 col-md-6 my-2">
                      <label for="last_name" class="small">Last Name</label>
                      <input
                        type="input"
                        data-recurly="last_name"
                        id="last_name"
                        name="last-name"
                        v-model="checkoutData.lastName"
                        class="form-control"
                      />
                      <small style="color: red" v-if="v$.checkoutData.lastName.$invalid && state.displayFormError">
                        Last name cannot be blank.
                      </small>
                    </div>
                    <div class="col-12 col-md-6 my-2">
                      <label for="postal_code" class="small">Postal Code</label>
                      <input
                        type="text"
                        data-recurly="postal_code"
                        id="postal_code"
                        name="postal-code"
                        v-model="checkoutData.postalCode"
                        class="form-control"
                        :maxlength="state.postalCodeLimit"
                        title="Postal Code (contains alphabets, numerals, spaces, hyphens)"
                      />
                      <small style="color: red" v-if="v$.checkoutData.postalCode.$invalid && state.displayFormError">
                        Postal code must be valid.
                      </small>
                    </div>
                    <CountrySelect :formError="v$.checkoutData.country.$invalid && state.displayFormError" />
                    <div
                      id="recurly-elements"
                      v-if="!state.checkout.hideBillingForm"
                      class="my-2 recurly-input"
                      :class="{
                        'mb-3': !(v$.state.checkout.validCard.$invalid && checkoutData.displayFormError),
                      }"
                    >
                      <label for="recurly-elements" class="small">Card</label>
                    </div>
                    <small style="color: red" class="mb-3" v-if="v$.state.checkout.validCard.$invalid && state.displayFormError">
                      Please provide required credit or debit card information.
                    </small>
                    <div v-if="state.checkout.turnstileSupported">
                      <div>
                        <VueTurnstile
                          :site-key="turnstileSiteKey"
                          v-model="state.checkout.captchaResponse"
                          size="flexible"
                          ref="turnstileWidget"
                          @expired="turnstileExpired"
                          @unsupported="turnstileUnsupported"
                          @error="turnstileError"
                        />
                      </div>
                      <small>
                        This site is protected by reCAPTCHA and the Google
                        <a href="https://policies.google.com/privacy">Privacy Policy</a> and
                        <a href="https://policies.google.com/terms">Terms of Service</a> apply.
                      </small>
                      <small style="color: red" class="mb-3" v-if="v$.state.checkout.captchaDone.$invalid && state.displayFormError">
                        Please complete the captcha challenge.
                      </small>
                      <small style="color: red" class="mb-3" v-if="state.checkout.turnstileError">
                        Captcha challenge encountered an error. Please reload the page or try another payment method.
                      </small>
                    </div>
                    <small style="color: red" class="mb-3" v-else>
                      Captcha challenge is not supported on this browser version - please use another payment method.
                    </small>
                    <div class="w-100 d-grid gap-2 mt-3" @click="showVuelidateErrors" @keydown="showVuelidateErrors">
                      <div class="alert alert-danger mt-3 stm-msg-checkout-error" role="alert" v-if="state.braintree.error">
                        <span>{{ state.braintree.error }}</span>
                      </div>
                      <PaymentErrorAlert payment-method-name="creditCard" :method-error="panel.methodError" :partial-success="panel.partialSuccess" />
                      <button
                        type="submit"
                        class="btn btn-primary btn-lg stm-btn-order-submit"
                        @click="subscribe"
                        :disabled="v$.$validationGroups.creditCard.$invalid"
                        v-if="!state.checkout.hideBillingForm"
                      >
                        Complete Order
                      </button>
                    </div>
                    <div class="row d-flex flex-nowrap justify-content-around w-100 text-center mt-3">
                      <div class="small col">
                        <img alt="secure" class="icon" :src="lockSvg" />
                        Encrypted and secure payments
                      </div>
                      <div class="small col align-top">
                        <img alt="money back" class="icon" :src="clockRotateLeftSvg" />
                        30-Day Money Back Guarantee
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="accordion-item col-12" v-if="enablePaypal && state.braintree.ready">
                <PaypalAccordionPanel
                  :disableButton="v$.$validationGroups.paypal.$invalid"
                  @hidePaypal="disablePaypal"
                  @paymentProcessing="showProcessingSpinner"
                  @paymentFailed="hideProcessingSpinner"
                  @showVuelidate="showVuelidateErrors"
                />
              </div>
              <div class="accordion-item col-12" v-if="state.showVenmo && state.braintree.ready">
                <VenmoAccordionPanel 
                  :disableButton="v$.$validationGroups.venmo.$invalid"
                  @paymentProcessing="showProcessingSpinner"
                  @paymentFailed="hideProcessingSpinner"
                  @showVuelidate="showVuelidateErrors"
                />
              </div>
              <div class="accordion-item col-12" @click="showVuelidateErrors" v-if="enableGooglePay && state.braintree.ready">
                <GooglePayAccordionPanel
                  :disableButton="v$.$validationGroups.googlePay.$invalid"
                  :formRef="form"
                  @paymentProcessing="showProcessingSpinner"
                  @paymentFailed="hideProcessingSpinner"
                  @showVuelidate="showVuelidateErrors"
                />
              </div>
            </div>
          </section>
        </div>
        <input type="hidden" data-recurly="token" name="recurly-token" />
        <input type="hidden" data-recurly="coupon" name="coupon" :value="checkoutData.coupon.inputtedCode.trim().toLowerCase()" />
        <input type="hidden" id="recurly-plan-speedify" data-recurly-subscription="0" data-recurly="plan" name="plan" v-model="checkoutData.plan" />
        <input
          type="hidden"
          id="recurly-plan-servers"
          ref="recurlyPlanServers"
          data-recurly-subscription="1"
          data-recurly="plan"
          name="plan"
          v-model="checkoutData.serverCode"
          v-if="serversAvailable"
        />
        <input type="hidden" data-recurly="vat_number" name="vat-number" v-model="state.vat.vatNumber" />
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, onMounted, onUnmounted, onErrorCaptured, watch, useTemplateRef, nextTick } from 'vue';
import * as Sentry from '@sentry/vue';
import { storeToRefs } from 'pinia';
import { useRouter, useRoute } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required, email, maxLength, requiredIf, sameAs } from '@vuelidate/validators';
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';
import VueTurnstile from 'vue-turnstile';

import { numberToCountryLocale } from '@/services/currencyTranslation';
import { capturePaymentError } from '@/services/sentry';
import { formatPlanCode } from '@/services/backend';
import CountrySelect from '@/components/CountrySelect.vue';
import PaypalAccordionPanel from '@/components/PaypalAccordionPanel.vue';
import VenmoAccordionPanel from '@/components/VenmoAccordionPanel.vue';
import ApplePayAccordionPanel from '@/components/ApplePayAccordionPanel.vue';
import GooglePayAccordionPanel from '@/components/GooglePayAccordionPanel.vue';
import UpsellCard from '@/components/UpsellCard.vue';
import { useAnalyticsStore } from '@/stores/analytics';
import CenteredBootstrapSpinner from '../components/ux/CenteredBootstrapSpinner.vue';
import ProcessingSpinner from '@/components/ux/ProcessingSpinner.vue';
import CartHeader from '@/components/CartHeader.vue';
import AccountSelection from '@/components/AccountSelection.vue';
import PaidSubAlert from '@/components/PaidSubAlert.vue';
import AddedTeamAlert from '@/components/AddedTeamAlert.vue';
import RemovedServerAlert from '@/components/RemovedServerAlert.vue';
import RouterCouponAlert from '@/components/RouterCouponAlert.vue';
import PlanChangeDialog from '@/components/PlanChangeDialog.vue';
import LoadingFailedAlert from '@/components/LoadingFailedAlert.vue';
import PaymentErrorAlert from '@/components/PaymentErrorAlert.vue';
import { useGeoIPStore } from '@/stores/geoip';
import { useCheckoutStore } from '@/stores/checkout';

import ccVisaSvg from '@/assets/svg/cc-visa.svg';
import ccAmexSvg from '@/assets/svg/cc-amex.svg';
import ccMastercardSvg from '@/assets/svg/cc-mastercard.svg';
import ccDiscoverSvg from '@/assets/svg/cc-discover.svg';
import lockSvg from '@/assets/svg/lock.svg';
import clockRotateLeftSvg from '@/assets/svg/clock-rotate-left.svg';

const router = useRouter();
const analyticsStore = useAnalyticsStore();
const geoIPStore = useGeoIPStore();
const checkoutStore = useCheckoutStore();
const {
  checkoutData,
  ignoreUsersPaidStatus,
  removeServersFromCheckout,
  paymentState,
  processPaymentToken,
  setPaymentProcessing,
  initializeCheckoutPricing,
} = checkoutStore;

const {
  checkoutPricing,
  freeTrialActive,
  couponIsVendorRouter,
  subscriptionProductRequiresCoupon,
  subscriptionProductEligibleForQuantityChange,
  serversAvailable,
  quantityLimitBump,
  serversLimitBump,
} = storeToRefs(checkoutStore);
const { quantityLimit } = checkoutStore;

// Inline "you've hit the max" popovers anchored to each quantity input. Kept as
// plain reactive state + a v-if div (no Bootstrap Toast/Popover) so it stays fully
// isolated from the Bootstrap-driven payment accordion below.
const QTY_LIMIT_POPOVER_MS = 6000;
const showQtyLimitPopover = ref(false);
const showServersLimitPopover = ref(false);
let qtyLimitTimer = null;
let serversLimitTimer = null;

watch(quantityLimitBump, () => {
  showQtyLimitPopover.value = true;
  clearTimeout(qtyLimitTimer);
  qtyLimitTimer = setTimeout(() => { showQtyLimitPopover.value = false; }, QTY_LIMIT_POPOVER_MS);
});

watch(serversLimitBump, () => {
  showServersLimitPopover.value = true;
  clearTimeout(serversLimitTimer);
  serversLimitTimer = setTimeout(() => { showServersLimitPopover.value = false; }, QTY_LIMIT_POPOVER_MS);
});

onUnmounted(() => {
  clearTimeout(qtyLimitTimer);
  clearTimeout(serversLimitTimer);
});

const form = useTemplateRef('form');
const turnstileWidget = ref(null);
const recurlyPlanServers = ref(null);
// Credit card purchase error lives in the store (like the wallet panels') so it is
// cleared along with everything else when the cart changes.
const panel = checkoutStore.panelState.creditCard;

const route = useRoute();
const enableIndividualServers = import.meta.env.VITE_ENABLE_INDIVIDUAL_SERVERS === 'true';
const enableVenmo = import.meta.env.VITE_ENABLE_VENMO === 'true';
const routerCouponPrefixes = import.meta.env.VITE_ROUTER_COUPON_PREFIXES;
// we'll reassign if applePay is enabled but browser doesn't support
const enableApplePay = ref(import.meta.env.VITE_ENABLE_APPLE_PAY === 'true');
const enablePaypal = ref(true);
const enableGooglePay = import.meta.env.VITE_ENABLE_GOOGLE_PAY === 'true';
const disableRemoveServerButton = computed(() => {
  return Number(route.query.addedTeam) === 1 || Number(route.query['removed-servers']) === 1;
});

/* eslint-disable vue/require-default-prop */
const props = defineProps({
  plan: String,
  coupon: String,
  quantity: Number,
  servers: Number,
  inapp: Number,
  couponFocus: Boolean,
});

const state = reactive({
  formVisibility: 'hidden',
  plan: {
    name: 'Speedify Unlimited Monthly Subscription',
    interval: '',
  },
  checkout: {
    success: false,
    hideBillingForm: false,
    validCard: false,
    disableSubscribeBtn: true,
    turnstileSupported: true,
    turnstileError: false,
    captchaDone: false,
    captchaResponse: '',
    couponPlanName: '',
    couponPlanPath: '',
    tosPrivacyAccepted: false,
  },
  pricing: {
    plan: 0,
    planNext: 0,
    servers: 0,
    taxes: 0,
    chargedAfterTrial: false,
    totalAfterTrial: 0,
    discount: 0,
    total: 0,
  },
  vat: {
    applicable: false,
    vatNumInputVisible: false,
    exemptionApplied: false,
    invalidNumber: false,
    vatNumber: null,
  },
  braintree: {
    error: '',
    // Set at the end of onMounted; gates the wallet panels so a Braintree outage at
    // mount surfaces one error instead of one per panel (see capturePaymentError).
    ready: false,
  },
  displayFormError: false,
  postalCodeLimit: 10,
  showCouponInput: props.couponFocus || false,
  showVenmo: enableVenmo,
  showVendorRouterCouponAlert: false,
});

const convertToCountryLocaleCurrency = (n) => numberToCountryLocale(n, checkoutData.country, checkoutData.currency);

// venmo only supports USD
watch(
  () => checkoutData.currency, 
  newCurrency => {
    state.showVenmo = newCurrency === 'USD' && enableVenmo;
  }
);

const postalCodeRequired = () => postcodeValidatorExistsForCountry(checkoutData.country);
const validPostalCode = value => {
  if (!postcodeValidatorExistsForCountry(checkoutData.country)) {
    return true;
  }
  return postcodeValidator(value, checkoutData.country);
};

const rules = computed(() => ({
  checkoutData: {
    firstName: { required },
    lastName: { required },
    postalCode: {
      required: requiredIf(postalCodeRequired),
      maxLength: maxLength(state.postalCodeLimit),
      validPostalCode,
    },
    country: { required },
    email: {
      required,
      email,
    },
    emailRepeat: {
      sameAsEmail: sameAs(computed(() => checkoutData.email)),
    },
  },
  state: {
    checkout: {
      captchaDone: {
        required,
        checked: sameAs(true),
      },
      validCard: {
        required,
        valid: sameAs(true),
      },
      tosPrivacyAccepted: {
        required,
        checked: sameAs(true),
      },
    },
  },
  $validationGroups: {
    creditCard: [
      'checkoutData.firstName',
      'checkoutData.lastName',
      'checkoutData.postalCode',
      'checkoutData.country',
      'checkoutData.email',
      'checkoutData.emailRepeat',
      'state.checkout.captchaDone',
      'state.checkout.validCard',
      'state.checkout.tosPrivacyAccepted',
    ],
    paypal: [
      'checkoutData.email',
      'checkoutData.emailRepeat',
      'state.checkout.tosPrivacyAccepted',
    ],
    venmo: [
      'checkoutData.email',
      'checkoutData.emailRepeat',
      'state.checkout.tosPrivacyAccepted',
    ],
    applePay: [
      'checkoutData.email',
      'checkoutData.emailRepeat',
      'state.checkout.tosPrivacyAccepted',
    ],
    googlePay: [
      'checkoutData.email',
      'checkoutData.emailRepeat',
      'state.checkout.tosPrivacyAccepted',
    ],
    freeCardlessTrial: [
      'checkoutData.email',
      'checkoutData.emailRepeat',
      'state.checkout.captchaDone',
      'state.checkout.tosPrivacyAccepted',
    ],
  },
}));

const v$ = useVuelidate(rules, { checkoutData, state });

// vuelidate shouldn't complain until the user tries to submit
const showVuelidateErrors = () => {
  state.displayFormError = true;
};

const showProcessingSpinner = () => {
  setPaymentProcessing(true);
};

const hideProcessingSpinner = () => {
  setPaymentProcessing(false);
};

let elements;

// Captcha stuff
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

const turnstileExpired = () => {
  state.checkout.captchaDone = false;
  state.checkout.captchaResponse = '';
};

const turnstileUnsupported = () => {
  state.checkout.turnstileSupported = false;
};

const turnstileError = () => {
  state.checkout.turnstileError = true;
};

onErrorCaptured((err) => {
  if (err === 'Failed to load Turnstile.' || err?.message === 'Failed to load Turnstile.') {
    state.checkout.turnstileError = true;
    return false;
  }
});

watch(() =>  state.checkout.captchaResponse, () => {
  if (state.checkout.captchaResponse !== '') {
    state.checkout.captchaDone = true;
  } else state.checkout.captchaDone = false;
});

watch(() => geoIPStore.details.country, () => {
  checkoutData.country = geoIPStore.details.country;
  if (checkoutData.country === 'US') {
    state.postalCodeLimit = 10;
  } else {
    state.postalCodeLimit = 15;
  }
});

watch(() => paymentState.purchaseFailed, () => {
  const invalidVat = typeof panel.methodError === 'string' && panel.methodError.includes('VAT number is invalid');
  if (paymentState.purchaseFailed && invalidVat) {
    checkoutPricing.value?.tax({ 'vat_number': '' })
      .catch(err => console.error('Recurly tax reset error:', err))
      .done(() => state.vat.exemptionApplied = false);
  }
});

const disableApplePay = () => {
  enableApplePay.value = false;
};

const disablePaypal = () => {
  enablePaypal.value = false;
};

const showCouponInput = () => {
  state.showCouponInput = true;
};

const applyCoupon = () => {
  // TODO: this is weird but let's check for vendor router coupons here as well
  // coupon codes don't make it to checkoutStore if they aren't validated by recurlyJS
  state.showVendorRouterCouponAlert = routerCouponPrefixes.split(',').some((prefix) => checkoutData.coupon.inputtedCode.startsWith(prefix));
  if (state.showVendorRouterCouponAlert) {
    checkoutData.coupon.routerCode = checkoutData.coupon.inputtedCode;
    return;
  }
  checkoutData.coupon.routerCode = '';
  checkoutPricing.value?.coupon(checkoutData.coupon.inputtedCode.toLowerCase())
    .catch(err => console.error('Recurly coupon pricing error:', err))
    .done();
};

const applyVatNumber = () => {
  checkoutPricing.value?.tax({ 'vat_number': state.vat.vatNumber })
    .catch(err => {
      console.error('VAT number validation failed:', err);
      state.vat.invalidNumber = true;
      state.vat.exemptionApplied = false;
    })
    .done((price) => {
      if (price.taxes[0]?.rate !== '0.0') {
        state.vat.invalidNumber = true;
        state.vat.exemptionApplied = false;
      } else {
        state.vat.invalidNumber = false;
        state.vat.exemptionApplied = true;
        checkoutData.vatNumber = state.vat.vatNumber;
      }
    });
};

const refreshCheckoutPricing = async () => {
  if (!window.recurly || !form.value) return;
  initializeCheckoutPricing(state);
  await nextTick();
  checkoutPricing.value.attach(form.value);
};

const handleRemoveServerClick = async () => {
  removeServersFromCheckout();
  state.pricing.servers = 0;
  try {
    await refreshCheckoutPricing();
  } catch (err) {
    Sentry.captureException(err);
  }
};

onMounted(async () => {
  try {
    if (!window.recurly || !window.braintree || !braintree?.dataCollector) {
      throw new Error('Payment scripts failed to load');
    }

    if (props.couponFocus) {
      // the form is visibility:hidden until Recurly pricing loads, so we can't
      // focus immediately; wait for formVisibility to flip to 'visible'
      const stopFocusWatch = watch(
        () => state.formVisibility,
        async (visibility) => {
          if (visibility === 'hidden') return;
          if (visibility === 'visible') {
            await nextTick();
            document.getElementById('redeem')?.focus();
          }
          stopFocusWatch();
        },
        { immediate: true },
      );
    }

    const clientInstance = await braintree.client.create({ authorization: import.meta.env.VITE_BRAINTREE_KEY });
    const dataCollectorInstance = await braintree.dataCollector.create({
       client: clientInstance,
       kount: true,
    });
    const { deviceData } = dataCollectorInstance;
    recurly.configure({
      publicKey: import.meta.env.VITE_RECURLY_KEY,
      fraud: {
        braintree: {
          deviceData,
        },
      },
      currency: geoIPStore.details.currency,
    });
  } catch (err) {
    if (err.message !== 'Payment scripts failed to load') {
      Sentry.captureException(err);
    }
    state.formVisibility = 'error';
    return;
  }

  initializeCheckoutPricing(state);
  elements = recurly.Elements();
  const cardElement = elements.CardElement({
    inputType: 'mobileSelect',
    style: {
      fontSize: '1em',
      placeholder: {
        color: 'gray !important',
        fontWeight: 'bold',
        content: {
          number: 'Card number',
          cvv: 'CVC',
        },
      },
      invalid: {
        fontColor: 'red',
      },
    },
  });

  cardElement.attach('#recurly-elements');

  cardElement.on('change', cardObj => {
    state.checkout.validCard = cardObj.valid;
  });

  const sleep = async (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  try {
    // Race condition: router.replace from isPaid watcher can trigger a Vue re-render
    // that temporarily nulls the form template ref while we're awaiting braintree setup.
    // Poll for the ref to become available with a timeout.
    let attempts = 0;
    while (!form.value && attempts < 10) {
      await sleep(100);
      attempts++;
    }
    if (!form.value) {
      console.warn('form ref unavailable after timeout');
      state.formVisibility = 'error';
      return;
    }
    if (!checkoutPricing.value) {
      console.warn('checkoutPricing unavailable at attach time');
      state.formVisibility = 'error';
      return;
    }
    checkoutPricing.value.attach(form.value);
    state.braintree.ready = true;
  } catch (err) {
    Sentry.captureException(err, {
      tags: {
        component: 'CheckoutCart',
        operation: 'recurly_pricing_attach',
      },
      extra: {
        formValueExists: !!form.value,
      },
    });
    state.formVisibility = 'error';
  }
});

// name fields are summarized (length/charset) instead of sent raw to keep PII out of Sentry
const describeName = value => ({
  length: value?.length ?? 0,
  hasNonAscii: /[^\x20-\x7E]/.test(value ?? ''),
});

const captureTokenError = err => {
  capturePaymentError('credit_card_tokenize', err, {
    // named to avoid Sentry's server-side scrubber, which nulls any field whose name contains "token"
    submittedBillingParams: {
      firstName: describeName(checkoutData.firstName),
      lastName: describeName(checkoutData.lastName),
      postalCode: describeName(checkoutData.postalCode), // summarized, not raw: keeps PII out of Sentry
      country: checkoutData.country,
      vatNumberLength: state.vat.vatNumber?.length ?? 0,
      plan: checkoutData.plan,
      serverCode: checkoutData.serverCode,
      quantity: checkoutData.quantity,
      servers: checkoutData.servers,
      coupon: checkoutData.coupon.inputtedCode,
      currency: checkoutData.currency,
      inapp: props.inapp,
    },
    cardElementValid: state.checkout.validCard,
    freeTrialActive: freeTrialActive.value,
  });
};

const subscribe = async () => {
  state.checkout.disableSubscribeBtn = true;
  if (state.checkout.hideBillingForm) {
    const result = await processPaymentToken(undefined, 'credit_card', analyticsStore.details, state.checkout.captchaResponse);
    if (result.error) {
      state.checkout.disableSubscribeBtn = false;
      turnstileWidget.value?.reset();
      state.checkout.captchaDone = false;
      panel.methodError = result.error.message;
      return;
    }
    if (result.status === 'partial-success') {
      state.checkout.disableSubscribeBtn = true;
      panel.partialSuccess = true;
      panel.methodError = result.messages || result.message;
    }
    return;
  }
  recurly.token(elements, form.value, async (err, token) => {
    if (err) {
      if (!err.fields?.length) {
        captureTokenError(err);
      }
      panel.methodError = err.message;
      paymentState.purchaseFailed = true;
      paymentState.processing = false;
      state.checkout.disableSubscribeBtn = false;
      turnstileWidget.value?.reset();
      state.checkout.captchaDone = false;
    } else {
      const result = await processPaymentToken(token, 'credit_card', analyticsStore.details, state.checkout.captchaResponse);
      if (result.error) {
        state.checkout.disableSubscribeBtn = false;
        turnstileWidget.value?.reset();
        state.checkout.captchaDone = false;
        panel.methodError = result.error.message;
      } else if (result.status === 'partial-success') {
        // keep this disabled?
        state.checkout.disableSubscribeBtn = true;
        panel.partialSuccess = true;
        panel.methodError = result.messages || result.message;
      }
    }
  });
};
</script>
<style scoped>
#redeem:focus {
  border: 2px solid #3fb4fc;
  -webkit-border-radius: 5px;
     -moz-border-radius: 5px;
          border-radius: 5px;

  -webkit-box-shadow: 0px 0px 6px #3fb4fc;
     -moz-box-shadow: 0px 0px 6px #3fb4fc;
          box-shadow: 0px 0px 6px #3fb4fc;
}
</style>
